import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MusicNotesSimple, Handshake, Eye, EyeSlash, WhatsappLogo, Copy, ArrowLeft } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Promotores() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [assigned, setAssigned] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !phone || !password) return toast.error("Llena todos los campos");
    if (password.length < 6) return toast.error("La contraseña debe tener al menos 6 caracteres");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/promoters/register`, { name, phone, password });
      setAssigned(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al registrarse");
    } finally { setLoading(false); }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(assigned.code);
      toast.success("Número copiado");
    } catch { toast.error("No se pudo copiar"); }
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(
      `🎵 ¡Crea una canción con TU historia en Tu MusiKa!\n${window.location.origin}/register\n\nAl registrarte, pon mi número de promotor: *${assigned.code}*`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      <div className="max-w-md w-full mx-auto px-4 py-10 flex-1 flex flex-col justify-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 w-fit px-3 py-2 -ml-3 rounded-full hover:bg-white/5 transition-colors"
          data-testid="promoter-back-home"
        >
          <ArrowLeft size={16} weight="bold" /> Volver al inicio
        </Link>
        <Link to="/" className="flex items-center gap-2 mb-10" data-testid="promoter-brand">
          <div className="w-10 h-10 rounded-full bg-[#FFB800] flex items-center justify-center">
            <MusicNotesSimple weight="fill" size={22} className="text-black" />
          </div>
          <span className="font-display font-bold text-2xl">Tu MusiKa</span>
        </Link>

        {assigned ? (
          <div className="text-center" data-testid="promoter-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#B4F34A] flex items-center justify-center mb-5">
              <Handshake weight="fill" size={30} className="text-black" />
            </div>
            <h1 className="font-display text-3xl font-bold mb-2">¡Bienvenido, {assigned.name}!</h1>
            <p className="text-white/60 mb-6">Tu número de promotor es:</p>
            <div className="rounded-2xl border-2 border-[#FFB800] bg-[#FFB800]/10 py-6 mb-3">
              <p className="font-display font-black text-6xl tracking-[0.2em] text-[#FFB800]" data-testid="promoter-code">
                {assigned.code}
              </p>
            </div>
            <p className="text-sm text-white/50 mb-8">
              Pide a tus clientes que escriban este número al crear su cuenta.
              Así sabremos que tú los trajiste. ¡Guárdalo bien!
            </p>
            <div className="space-y-3">
              <button
                onClick={shareWhatsApp}
                className="w-full py-4 rounded-full bg-[#25D366] hover:bg-[#1FB858] text-black font-semibold flex items-center justify-center gap-2 transition-all active:scale-95"
                data-testid="promoter-share-whatsapp"
              >
                <WhatsappLogo weight="fill" size={20} /> Invitar clientes por WhatsApp
              </button>
              <button
                onClick={copyCode}
                className="w-full py-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 font-semibold flex items-center justify-center gap-2"
                data-testid="promoter-copy-code"
              >
                <Copy size={18} /> Copiar mi número
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-2">
              <Handshake weight="fill" size={24} className="text-[#B4F34A]" />
              <h1 className="font-display text-3xl font-bold">Promotores</h1>
            </div>
            <p className="text-white/50 mb-8">
              Regístrate como promotor y recibe tu número único de 4 dígitos.
              Gana recomendando Tu MusiKa en tu zona.
            </p>
            <form onSubmit={submit} className="space-y-4" data-testid="promoter-form">
              <div>
                <label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Tu nombre</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nombre completo"
                  className="w-full px-4 py-3 rounded-xl bg-[#121212] border border-white/10 focus:border-[#FFB800] focus:outline-none transition-colors text-white placeholder-white/30"
                  data-testid="promoter-name"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Teléfono</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="55 1234 5678 (10 dígitos)"
                  className="w-full px-4 py-3 rounded-xl bg-[#121212] border border-white/10 focus:border-[#FFB800] focus:outline-none transition-colors text-white placeholder-white/30"
                  data-testid="promoter-phone"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Contraseña</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className="w-full px-4 pr-12 py-3 rounded-xl bg-[#121212] border border-white/10 focus:border-[#FFB800] focus:outline-none transition-colors text-white placeholder-white/30"
                    data-testid="promoter-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/5 transition-colors"
                  >
                    {showPassword ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-full bg-[#B4F34A] hover:bg-[#A0E03A] disabled:opacity-40 text-black font-semibold transition-all active:scale-95"
                data-testid="promoter-submit"
              >
                {loading ? "Registrando…" : "Obtener mi número de promotor"}
              </button>
            </form>
            <p className="text-center text-sm text-white/50 mt-6">
              ¿Quieres crear una canción?{" "}
              <Link to="/register" className="text-[#FFB800] hover:underline">Crear cuenta de cliente</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
